import { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { styled } from '@mui/material/styles';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  RadioGroup,
  Radio,
  MenuItem,
  Select,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ArrowDropDown } from '@mui/icons-material';
import PropTypes from 'prop-types';

import InfoToolTip from '../../../../components/layout/atoms/InfoToolTip';
import SmallButton from '../../../../components/layout/atoms/SmallButton';
import IconHeading from '../../../../components/layout/molecules/IconHeading';
import NationalIDField from '../../../management/PatientSignUp/components/NationalIDField';
import EmailOrPhoneField from '../../../management/PatientSignUp/components/EmailOrPhoneField';

import { EditIcon, FileTextIcon, UserIcon, UserXIcon, XIcon } from '../../../../resources/icons';
import { EMAIL, PHONE_NUMBER } from '../../../../constants/validations';
import {
  ETHNIC_CODE_ENUMS,
  ETHNIC_CODE_ENUMS_STR,
  GENDER_STR_ENUMS,
  PATIENT,
  GERMANY,
  IS_OPTIONAL_EMAIL,
} from '../../../../constants/constants';
import { setSnackbar } from '../../../../actions/controls';
import {
  getMyDetails,
  getPatientDetails,
  updatePatientDetails,
  updatePatientEmail,
  updatePatientPhoneNumber,
} from '../../../../actions/patients';
import {
  getDeployRegion,
  getRegionSpecificMetaData,
  getRegionSpecificEthnicity,
  getRegionSpecificSignUpFormFieldConfigs,
} from '../../../../utils/configurationHelpers';
import { stringCapitalize, stripString, testDOB, getTranslationValueFromKey } from '../../../../utils/helpers';
import { datePickerLangText } from '../../../../i18n';

const StyledIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'autoGenerateEmail',
})(({ theme, autoGenerateEmail = false }) => ({
  backgroundColor: autoGenerateEmail ? theme.palette.background.dark : theme.palette.background.grey,
  border: autoGenerateEmail ? `2px solid ${theme.palette.primary.light}` : `1px solid ${theme.palette.border.dark}`,
  width: '44px',
  height: '40px',
  borderRadius: '4px',
  alignContent: 'center',
  alignItems: 'center',
  color: autoGenerateEmail ? theme.palette.primary.main : theme.palette.text.main,

  '&:hover': {
    color: theme.palette.primary.light,
    backgroundColor: theme.palette.background.default,
    border: '2px solid',
  },
}));

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  color: theme.palette.grey.dark,
}));

const ProfileDetails = (props) => {
  let {
    user,
    title,
    patientDetails,
    disallowEdit,
    selfUpdated,
    hidePatientDetails,
    updatePatientDetails,
    updatePatientEmail,
    updatePatientPhoneNumber,
    getPatientDetails,
    getMyDetails,
    setSnackbar,
  } = props;

  const initialProfileValues = {
    account_number: (user?.role === PATIENT ? user?.account_number : patientDetails.user?.account_number) || '',
    email: patientDetails.user?.email || '',
    first_name: patientDetails.user?.user_contact.first_name || '',
    last_name: patientDetails.user?.user_contact.last_name || '',
    postcode: patientDetails.user?.user_contact.address.postcode || '',
    phone_number: patientDetails?.user?.phone_number || '',
    gender: patientDetails?.demographics?.gender || '',
    ethnicity: patientDetails?.demographics?.ethnicity || '',
    date_of_birth: patientDetails?.demographics?.date_of_birth || '',
    national_id: patientDetails?.demographics?.national_id || '',
    metadata: patientDetails?.metadata || {},
  };
  const SignUpFormFieldConfigs = getRegionSpecificSignUpFormFieldConfigs();
  const isGermany = getDeployRegion() === GERMANY;

  const formatProfileValues = (values, editMode) => {
    // Display '-' as placeholder for empty value for patient profile
    let temp = values?.metadata ? values : { ...values };
    for (const key in temp) {
      if ((temp[key] === null || temp[key] === '' || temp[key] === '-') && key !== 'date_of_birth')
        temp[key] = editMode ? '' : '-';

      if (key === 'metadata') {
        for (const prop in temp[key]) {
          if (temp[key][prop] === null || temp[key][prop] === undefined) {
            temp[key][prop] = editMode ? '' : '-';
          }
        }
      }
    }

    return temp;
  };

  const staticWords = useSelector((state) => state.handlingTranslation.words);
  const { t, i18n } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [autoGenerateEmail, setAutoGenerateEmail] = useState(initialProfileValues?.email === '-' ? true : false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    phone_number: false,
    national_id: false,
  });
  const [dateError, setDateError] = useState('');
  const [profileValues, setProfileValues] = useState(formatProfileValues(initialProfileValues, editMode));

  const signUpFormFieldConfigs = getRegionSpecificSignUpFormFieldConfigs();
  const ethnicityElements = getRegionSpecificEthnicity();
  const regionMetadata = getRegionSpecificMetaData();

  const handlingDOBErrors = (err) => {
    if (err && ['invalidDate', 'disableFuture'].includes(err)) {
      setDateError('Invalid_Input'); // Show error if date is invalid
    } else {
      setDateError('');
    }
  };

  const handleEdit = () => {
    setProfileValues(formatProfileValues(profileValues, true));
    setEditMode(true);
  };

  const handleSetError = (fieldName, error) => {
    setErrors({ ...errors, [fieldName]: error });
  };

  const handleReset = () => {
    setDateError('');
    setEditMode(false);
    setProfileValues(formatProfileValues(initialProfileValues));
    setAutoGenerateEmail(initialProfileValues?.email === '-' ? true : false);
  };

  const handleProfileChange = (field, event) => {
    let newValue = event.target.value;
    if (field === 'postcode') {
      let output = stripString(
        event.target.value,
        SignUpFormFieldConfigs.postcode.excludedChars,
        SignUpFormFieldConfigs.postcode.maxLength
      );

      newValue = output;
    }
    setProfileValues({ ...profileValues, [field]: newValue });
  };

  const handleMetadataChange = (e) => {
    const { name, value } = e.target;

    setProfileValues({
      ...profileValues,
      metadata: {
        ...profileValues.metadata,
        [name]: value,
      },
    });
  };

  const formatDate = (date) => {
    if (!date) return null;
    if (isNaN(date.getDate())) return null;
    let month = date.getMonth() + 1 + '';
    let day = date.getDate() + '';
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    let formatted_date = date.getFullYear() + '-' + month + '-' + day;
    return formatted_date;
  };

  const handleDateChange = (date, target) => {
    setProfileValues({ ...profileValues, [target]: date });
  };

  const filterInputsByFields = (inputs, fields) => {
    let res = {};
    for (const key in inputs) {
      if (
        fields.includes(key) &&
        (inputs[key] !== null || inputs[key] !== '-') &&
        inputs[key] !== initialProfileValues[key]
      ) {
        res[key] = inputs[key];
      }
    }

    return res;
  };

  const renderConditionalToolTip = (innerField, title, content) => {
    if (editMode) {
      return (
        <Grid>
          <InfoToolTip placement="bottom-start" title={title} content={content}>
            {innerField}
          </InfoToolTip>
        </Grid>
      );
    } else {
      return innerField;
    }
  };

  const handleAutoGenerateEmailChange = () => {
    if (!autoGenerateEmail) {
      setProfileValues({ ...profileValues, email: 'null' });
    } else {
      setProfileValues({ ...profileValues, email: patientDetails.user?.email || '' });
    }
    setAutoGenerateEmail(!autoGenerateEmail);
  };

  const validateFields = (fields = []) => {
    return !fields.find((field) => errors[field]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateFields(Object.keys(errors))) return setSnackbar(t('Please_correct_the_errors_above_'), 'error');

    const clone = { ...profileValues, metadata: { ...regionMetadata, ...profileValues?.metadata } };
    clone.first_name = stringCapitalize(clone.first_name);
    clone.last_name = stringCapitalize(clone.last_name);

    //If postcode is not empty and is not valid pattern, display error snackbar
    if (
      clone?.postcode &&
      clone.postcode?.length > 0 &&
      !isGermany &&
      !SignUpFormFieldConfigs.postcode.pattern.test(clone.postcode)
    ) {
      return setSnackbar(t('Please_enter_a_valid_postcode_'), 'error');
    }

    if (isGermany) {
      if (!!clone?.national_id) {
        clone.national_id = clone.national_id.toUpperCase();
      }

      // Set consent to false for DE region if value is null or undefined
      if (clone?.metadata?.is_consented === null || clone?.metadata?.is_consented === undefined) {
        clone.metadata.is_consented = false;
      }
    }

    //Only format the date if date is being changed
    if (clone.date_of_birth !== initialProfileValues?.date_of_birth) {
      clone.date_of_birth = formatDate(clone?.date_of_birth);
    }
    if (!clone.gender || clone.gender === '-') {
      clone.gender = 0;
    }
    if (!clone.ethnicity || clone.ethnicity === '-') {
      clone.ethnicity = 0;
    }
    if (!clone.phone_number || clone.phone_number === '-') {
      clone.phone_number = '';
    }
    if (typeof clone.email === 'string' && clone.email.trim() === '-') {
      return setSnackbar(t('Email_is_required'), 'error');
    }
    if (!!clone?.date_of_birth && !testDOB(clone.date_of_birth)) {
      setDateError('Invalid_Input'); // Show error if date is invalid
      return setSnackbar(t('Please_correct_the_errors_above_'), 'error');
    }
    try {
      setIsSaving(true);
      await updatePatientDetails(patientDetails?.id, clone, selfUpdated);
      if (initialProfileValues?.email !== clone?.email) await updatePatientEmail(patientDetails?.id, clone?.email);
      if (initialProfileValues?.phone_number !== clone?.phone_number) {
        const response = await updatePatientPhoneNumber(
          patientDetails?.id,
          !!clone?.phone_number ? clone?.phone_number : null
        );
        if (response?.status === 400) return;
      }
      setEditMode(false);
      setIsSaving(false);
      setProfileValues(formatProfileValues(clone, false));
    } catch (e) {
      setIsSaving(false);
    } finally {
      setIsSaving(false);
    }
  };
  return (
    <Box>
      <form autoComplete="off">
        <Grid container gap={3}>
          <Grid item container justifyContent="space-between" alignItems="center">
            <Grid item>
              <IconHeading title={title} icon={<UserIcon />} />
            </Grid>
            <Grid>
              {disallowEdit ? null : editMode ? (
                <Grid item container display="flex" gap={1}>
                  <Grid item>
                    <SmallButton startIcon={<FileTextIcon />} showLoading={isSaving} onClick={handleSubmit}>
                      {staticWords.Save}
                    </SmallButton>
                  </Grid>
                  <Grid item>
                    <SmallButton startIcon={<XIcon />} disabled={isSaving} onClick={handleReset}>
                      {staticWords.Cancel}
                    </SmallButton>
                  </Grid>
                </Grid>
              ) : (
                <Grid item>
                  <SmallButton startIcon={<EditIcon />} showLoading={isSaving} onClick={handleEdit}>
                    {staticWords.Edit_Data}
                  </SmallButton>
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={disallowEdit ? 2 : 3}>
            <Grid item xs={isGermany ? 12 : 6}>
              {renderConditionalToolTip(
                <TextField
                  disabled
                  fullWidth
                  type="accountNumber"
                  label={staticWords.Account_Number}
                  id="patient-account-number"
                  name="accountNumber"
                  value={profileValues.account_number}
                  InputProps={{ disableUnderline: disallowEdit }}
                />,
                staticWords.Not_available,
                staticWords.Patient_s_account_number_is_unique_and_not_available_for_modification_
              )}
            </Grid>
            {!isGermany && (
              <Grid item mb={disallowEdit && -2} xs={6}>
                <NationalIDField
                  fullWidth
                  label={staticWords[getTranslationValueFromKey(signUpFormFieldConfigs.nationalID.label)]}
                  name="national_id"
                  country={patientDetails?.user?.country}
                  initialValue={profileValues.national_id}
                  pattern={signUpFormFieldConfigs.nationalID.pattern}
                  disabled={!editMode || isSaving || selfUpdated || !signUpFormFieldConfigs.nationalID.allowEdit}
                  disableUnderline={disallowEdit}
                  helperText={editMode ? signUpFormFieldConfigs.nationalID.helperText : disallowEdit ? null : ' '}
                  maxLength={signUpFormFieldConfigs.nationalID.maxLength}
                  excludedChars={signUpFormFieldConfigs.nationalID.excludedChars}
                  resetValues={editMode}
                  onError={(error) => handleSetError('national_id', error)}
                  onChange={(event) => handleProfileChange('national_id', event)}
                />
              </Grid>
            )}
            {!hidePatientDetails && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={staticWords.First_Name}
                  name="first_name"
                  id="patient-first-name"
                  value={profileValues.first_name}
                  disabled={!editMode || isSaving}
                  InputProps={{ disableUnderline: disallowEdit }}
                  onChange={(event) => handleProfileChange('first_name', event)}
                />
              </Grid>
            )}
            {!hidePatientDetails && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={staticWords.Last_Name}
                  name="last_name"
                  id="patient-last-name"
                  value={profileValues.last_name}
                  disabled={!editMode || isSaving}
                  InputProps={{ disableUnderline: disallowEdit }}
                  onChange={(event) => handleProfileChange('last_name', event)}
                />
              </Grid>
            )}
            {!hidePatientDetails &&
              (IS_OPTIONAL_EMAIL ? (
                <Grid item xs={6}>
                  {!editMode ? (
                    <EmailOrPhoneField
                      type={EMAIL}
                      name="email"
                      initialValue={
                        !autoGenerateEmail
                          ? profileValues.email === '-'
                            ? ''
                            : profileValues.email ?? '-'
                          : t('LB_no_email_provided')
                      }
                      showValidationError
                      fullWidth
                      disabled
                      sx={autoGenerateEmail ? { fontStyle: 'italic' } : {}}
                      autoComplete="off"
                      InputProps={{ disableUnderline: !!disallowEdit }}
                      onError={(e) => handleSetError('email', e)}
                      onChange={(e) => handleProfileChange('email', e)}
                    />
                  ) : (
                    <Grid item container alignItems="flex-end" spacing={1}>
                      <Grid item xs={10}>
                        <EmailOrPhoneField
                          type={EMAIL}
                          name="email"
                          initialValue={
                            !autoGenerateEmail
                              ? profileValues.email === '-'
                                ? ''
                                : profileValues.email ?? '-'
                              : t('LB_no_email_provided')
                          }
                          sx={autoGenerateEmail ? { fontStyle: 'italic' } : {}}
                          showValidationError
                          fullWidth
                          autoComplete="off"
                          disabled={autoGenerateEmail || isSaving}
                          InputProps={{ disableUnderline: !!disallowEdit }}
                          onError={(error) => handleSetError('email', error)}
                          onChange={(e) => handleProfileChange('email', e)}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <StyledIconButton
                          autoGenerateEmail={autoGenerateEmail}
                          aria-label="autoGenerateEmail"
                          onClick={() => handleAutoGenerateEmailChange()}
                        >
                          <UserXIcon width={18} height={18} />
                        </StyledIconButton>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Grid item xs={6}>
                  <EmailOrPhoneField
                    type={EMAIL}
                    name="email"
                    initialValue={profileValues.email}
                    showValidationError
                    fullWidth
                    autoComplete="off"
                    disabled={!editMode || isSaving || selfUpdated}
                    InputProps={{ disableUnderline: !!disallowEdit }}
                    onError={(e) => handleSetError('email', e)}
                    onChange={(e) => handleProfileChange('email', e)}
                  />
                </Grid>
              ))}
            {!hidePatientDetails && (
              <Grid item xs={6}>
                <EmailOrPhoneField
                  defaultCountry={user.country}
                  type={PHONE_NUMBER}
                  name="phone_number"
                  initialValue={profileValues.phone_number}
                  showValidationError
                  fullWidth
                  autoComplete="off"
                  disabled={!editMode || isSaving || selfUpdated}
                  InputProps={{ disableUnderline: !!disallowEdit }}
                  onError={(e) => handleSetError('phone_number', e)}
                  onChange={(e) => handleProfileChange('phone_number', e)}
                />
              </Grid>
            )}
            {/* date_of_birth */}
            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={datePickerLangText(i18n.language)}>
                <DesktopDatePicker
                  fullWidth
                  renderInput={(textFieldProps) => {
                    textFieldProps.inputProps.value = props.disallowEdit
                      ? textFieldProps.inputProps.value || '-'
                      : textFieldProps.inputProps.value;
                    return (
                      <TextField
                        {...textFieldProps}
                        sx={{ width: '100%' }}
                        error={!!dateError}
                        helperText={t(dateError) || ''}
                      />
                    );
                  }}
                  OpenPickerButtonProps={{
                    style: { display: disallowEdit && 'none' },
                  }}
                  label={staticWords.Date_of_Birth}
                  name="date_of_birth"
                  id="patient-date-of-birth"
                  value={profileValues.date_of_birth}
                  disableFuture
                  disabled={!editMode || isSaving}
                  InputProps={{ disableUnderline: disallowEdit }}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  inputFormat="dd-MM-yyyy"
                  autoOk={true}
                  onChange={(date) => {
                    if (!date || isNaN(date.getTime()) || date > new Date()) {
                      setDateError('Invalid_Input'); // Show error if date is invalid
                    } else {
                      setDateError(''); // Clear error when valid
                    }
                    handleDateChange(date, 'date_of_birth');
                  }}
                  onError={(error) => handlingDOBErrors(error)}
                />
              </LocalizationProvider>
            </Grid>
            {!isGermany && (
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="text"
                  label={staticWords.Post_Code}
                  name="postcode"
                  id="patient-postcode"
                  value={profileValues.postcode}
                  disabled={!editMode || isSaving}
                  InputProps={{ disableUnderline: disallowEdit }}
                  inputProps={{
                    maxLength: signUpFormFieldConfigs.postcode.maxLength,
                    style: { textTransform: 'uppercase' },
                  }}
                  helperText={editMode ? signUpFormFieldConfigs.postcode.helperText : disallowEdit ? null : ' '}
                  onChange={(event) => handleProfileChange('postcode', event)}
                />
              </Grid>
            )}
            {!isGermany && (
              <Grid item mt={!disallowEdit && -1} xs={6}>
                <FormControl fullWidth>
                  <StyledInputLabel id="select-gender-label">{staticWords.Biological_Sex}</StyledInputLabel>
                  <Select
                    defaultValue=""
                    labelId="select-gender-label"
                    name="gender"
                    id="patient-gender"
                    value={profileValues.gender}
                    disabled={!editMode || isSaving || disallowEdit}
                    disableUnderline={disallowEdit}
                    IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                    renderValue={(value) => {
                      if (!GENDER_STR_ENUMS?.[value]) return '-';
                      return staticWords[getTranslationValueFromKey(GENDER_STR_ENUMS[value])];
                    }}
                    onChange={(e) => handleProfileChange('gender', e)}
                  >
                    <MenuItem value={0}>-</MenuItem>
                    {Object.entries(GENDER_STR_ENUMS).map(([key, gender]) => (
                      <MenuItem key={key} value={key}>
                        {staticWords[getTranslationValueFromKey(gender)]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {!isGermany && (
              <Grid item mt={!disallowEdit && -1} xs={6}>
                <FormControl fullWidth>
                  <StyledInputLabel id="select-ethnicity-label">{staticWords.Ethnicity}</StyledInputLabel>
                  <Select
                    defaultValue=""
                    labelId="select-ethnicity-label"
                    name="ethnicity"
                    id="patient-ethnicity"
                    value={profileValues.ethnicity}
                    disabled={!editMode || isSaving || disallowEdit}
                    disableUnderline={disallowEdit}
                    IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                    renderValue={(value) => {
                      if (!ETHNIC_CODE_ENUMS_STR?.[value]) return '-';
                      return staticWords[getTranslationValueFromKey(ethnicityElements[ETHNIC_CODE_ENUMS_STR[value]])];
                    }}
                    onChange={(event) => handleProfileChange('ethnicity', event)}
                  >
                    <MenuItem value={0}>-</MenuItem>
                    {Object.keys(ethnicityElements || {}).map((ethnicity) => {
                      return (
                        <MenuItem value={ETHNIC_CODE_ENUMS[ethnicity]} key={ethnicity}>
                          {staticWords[getTranslationValueFromKey(ethnicityElements[ethnicity])]}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {isGermany && (
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <StyledInputLabel id="select-consent-label">{staticWords.Client_consent}</StyledInputLabel>
                  <Select
                    defaultValue=""
                    labelId="select-consent-label"
                    name="is_consented"
                    id="patient-consent"
                    value={profileValues.metadata?.is_consented || false}
                    disabled={!editMode || isSaving || disallowEdit}
                    disableUnderline={disallowEdit}
                    IconComponent={() => (disallowEdit ? null : <ArrowDropDown />)}
                    renderValue={(value) => {
                      return value === true ? staticWords.Yes : staticWords.No;
                    }}
                    onChange={(event) => handleMetadataChange(event)}
                  >
                    <MenuItem value={true}>{staticWords.Yes}</MenuItem>
                    <MenuItem value={false}>{staticWords.No}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

ProfileDetails.propTypes = {
  user: PropTypes.any.isRequired,
  title: PropTypes.any,
  patientDetails: PropTypes.any.isRequired,
  disallowEdit: PropTypes.any,
  selfUpdated: PropTypes.any,
  hidePatientDetails: PropTypes.any,
  updatePatientDetails: PropTypes.func.isRequired,
  updatePatientEmail: PropTypes.func,
  updatePatientPhoneNumber: PropTypes.func,
  setSnackbar: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  updatePatientDetails,
  updatePatientEmail,
  updatePatientPhoneNumber,
  getPatientDetails,
  getMyDetails,
  setSnackbar,
})(ProfileDetails);
